import * as Papa from 'papaparse';
import { read, utils } from 'xlsx';
import { PERSONNEL, NONPERSONNEL, FUND, ERROR, EDIT, VALIDATION } from '../../../static/keyWords.js';
import {
  DURATION_TYPE_ID,
  EDIT_TYPE_LABELS,
  NONPERSONNEL_OBJECT_TYPE_ID,
  PERSONNEL_OBJECT_TYPE_ID,
  USER_ROLE,
} from '../../../static/constants.js';
import { messageManager } from '../../System/messageManager/messageManager.js';
import { normalizeDurationTypeValue } from './ValidationStep/columnValidators.js';
import { roundToTwoDecimals } from '../../reusable/reusableFunctions.js';
import CustomFloatingFilter from './MappingStep/CustomFloatingFilter.js';
import { REDUCER_ACTION_TYPES } from '../../../store/Reducer.js';
import { ToastComponent } from '@tylertech/forge';
import {
  //validateAccountCodeColumn,
  validateAccountIdColumn,
  validateAccountTypeColumn,
  validateBudgetNameColumn,
  validateCostCenterColumn,
  validateDurationTypeColumn,
  validateEditColumn,
  validateFundTypeColumn,
  validateItemIdColumn,
  validateNumberOfItemsColumn,
  validateOneTimeCostColumn,
  validateOngoingCostColumn,
  validateYearColumn,
} from './ValidationStep/columnValidators.js';
import { validateAccounts } from '../../../services/accountService.js';
import { CHUNK_SIZE } from '../../../static/constants.js';
import { 
  emitBudgetUploadStatusReportChunk, 
  emitDocumentLoading 
} from '../../../services/downloadService.js';
import { SESSION_STORAGE_KEYS } from '../../../static/localStorageStrings.js';
import { setActiveCostCenters } from '../../../services/costCentersService.js';

export const BUDGET_NAME_COLUMN = 'BudgetName';
export const YEAR_COLUMN = 'Year';
export const ITEM_ID_COLUMN = 'ItemID';
export const ACCOUNT_ID_COLUMN = 'AcctID';
export const ACCOUNT_CODE_COLUMN = 'AcctCode';
export const ITEM_META_1_COLUMN = 'ItemMeta1';
export const ITEM_META_2_COLUMN = 'ItemMeta2';
export const ITEM_META_3_COLUMN = 'ItemMeta3';
export const NAME_META__COLUMN = 'NameMeta';
export const ITEM_COMMENTS_COLUMN = 'ItemComments';
export const ACCOUNT_TYPE_COLUMN = 'Account type';
export const DURATION_TYPE_COLUMN = 'Duration type';
export const ONGOING_COST_COLUMN = 'Ongoing_cost';
export const ONETIME_COST_COLUMN = 'Onetime_cost';
export const NUMBER_OF_ITEMS_COLUMN = 'Number of items';
export const COST_NUMERIC_REGEX = /^-?\d{1,13}(\.\d{1,2})?$/;
export const DURATION_TYPES = { ONGOING: 'ongoing', ONE_TIME: 'onetime' };
export const DURATION_TYPE_LABELS = { ONGOING: 'Ongoing', ONE_TIME: 'One time' };
export const ACCOUNT_TYPES = { EXPENSE: 'expense', REVENUE: 'revenue', };
export const ACCOUNT_TYPE_LABELS = { EXPENSE: 'Expense', REVENUE: 'Revenue', };
export const SINGLE_DATATYPE_UPLOAD_LAST_STEP = 1;
export const MULTI_DATATYPE_UPLOAD_LAST_STEP = 3;

const getUniqueProps = (costModel) => {
  return [
    'Budget name',
    'Year',
    'AcctCode',
    'Account type',
    'Duration type',
    FUND,
    ...costModel.organizationLevelTypes.map((level) => level.name),
    ...costModel.objectCategoryTypes.map((category) => category.name),
  ];
};

export const uploadSteps = [
  {
    step: 'Personnel',
    label: 'Personnel mapping',
  },
  {
    step: 'Personnel',
    label: 'Personnel validation',
  },
  {
    step: 'NonPersonnel',
    label: 'NonPersonnel mapping',
  },
  {
    step: 'NonPersonnel',
    label: 'NonPersonnel validation',
  },
];

export const extractData = async (
  uploadedFiles,
  setParsedData,
  setExcelColumns,
  nameConventions,
  setOriginalData,
  setDataTypes,
  setUploadedFiles
) => {
  if (!uploadedFiles)
    return {
      success: false,
      message: messageManager(nameConventions).BUDGET_UPLOAD.PARSING.NO_FILE,
    };

  const firstObj = uploadedFiles[0];
  const fileFormat = uploadedFiles[0]?.file?.name?.split('.').pop().toLowerCase();
  
  if (fileFormat === 'csv')
    return await parseCsvFiles(
      uploadedFiles,
      setParsedData,
      setExcelColumns,
      nameConventions,
      setOriginalData,
      setDataTypes
    );
  if (fileFormat === 'xlsx')
    return await parseExcelFile(
      firstObj.file,
      setParsedData,
      setExcelColumns,
      nameConventions,
      setOriginalData,
      setDataTypes,
      setUploadedFiles
    );

  return {
    success: false,
    message:
      messageManager(nameConventions).BUDGET_UPLOAD.PARSING.UNSUPPORTED_FORMAT,
  };
};

const parseCsvFiles = async (
  fileObjs,
  setParsedData,
  setExcelColumns,
  nameConventions,
  setOriginalData,
  setDataTypes
) => {
  try {
    const xlsxData = {};
    const excelCols = {};
    const dataTypes = [];

    for (const fileObj of fileObjs) {
      const { file, personnelType } = fileObj;
      if (!file || !personnelType) continue;

      const results = await papaParsePromise(file);
      const headers = {};
      results.meta.fields.forEach(
        (field) => (headers[field.toLowerCase()] = field),
      );
      const data = results.data;
      data.forEach(row=>{
        row[ONETIME_COST_COLUMN] = typeof row[ONETIME_COST_COLUMN] === 'string' ? row[ONETIME_COST_COLUMN].replace(/[^0-9.-]+/g, '') : row[ONETIME_COST_COLUMN];
        roundToTwoDecimals(row[ONETIME_COST_COLUMN]);
        row[ONGOING_COST_COLUMN] = typeof row[ONGOING_COST_COLUMN] === 'string' ? row[ONGOING_COST_COLUMN].replace(/[^0-9.-]+/g, '') : row[ONGOING_COST_COLUMN];
        roundToTwoDecimals(row[ONGOING_COST_COLUMN]);
      });

      if (personnelType === PERSONNEL) {
        xlsxData[PERSONNEL] = data;
        excelCols[PERSONNEL] = headers;
        dataTypes.push(PERSONNEL);
      } else if (personnelType === NONPERSONNEL) {
        xlsxData[NONPERSONNEL] = data;
        excelCols[NONPERSONNEL] = headers;
        dataTypes.push(NONPERSONNEL);
      }
    }

    if (!dataTypes.length) {
      return {
        success: false,
        message:
          messageManager(nameConventions).BUDGET_UPLOAD.PARSING
            .CSV_MISSING_TYPE,
      };
    }

    xlsxData[PERSONNEL] &&
        xlsxData[PERSONNEL].forEach((x) => (x.rowId = crypto.randomUUID()));
    xlsxData[NONPERSONNEL] &&
        xlsxData[NONPERSONNEL].forEach((x) => (x.rowId = crypto.randomUUID()));

    setParsedData(xlsxData);
    setOriginalData(structuredClone(xlsxData));
    setExcelColumns(excelCols);
    setDataTypes(dataTypes);

    return { success: true };
  } catch (err) {
    return {
      success: false,
      message:
        messageManager(nameConventions).BUDGET_UPLOAD.PARSING.INVALID_CSV,
    };
  }
};

const papaParsePromise = async (file) => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      skipEmptyLines: true,
      header: true,
      worker: false,
      complete: function (results) {
        if (results.data.length === 0) {
          reject({ error: 'No data in file ' + file.name, type: ERROR });
        } else if (results.errors.length > 0) {
          reject({
            error: results.errors[0].message,
            type: results.errors[0].type,
          });
        }
        const resultsDataKeys = Object.keys(
          results.data[results.data.length - 1],
        );
        if (
          resultsDataKeys.length < results.meta.fields.length ||
          (resultsDataKeys.length === 1 &&
            results.data[0][resultsDataKeys[0]] === '')
        ) {
          results.data = results.data.slice(0, -1);
        }
        resolve(results);
      },
    });
  });
};

const parseExcelFile = (
  file,
  setParsedData,
  setExcelColumns,
  nameConventions,
  setOriginalData,
  setDataTypes,
  setUploadedFiles
) => {
  return new Promise((resolve) => {
    const csvArrs = {};
    const excelCols = {};

    const reader = new FileReader();

    reader.onload = function (e) {
      const data = new Uint8Array(e.target.result);
      const workbook = read(data, { type: 'array' });

      const hasPersonnel =
        workbook.Sheets[PERSONNEL] && rowsExist(workbook, PERSONNEL);
      const hasNonPersonnel =
        workbook.Sheets[NONPERSONNEL] && rowsExist(workbook, NONPERSONNEL);

      if (!hasPersonnel && !hasNonPersonnel) {
        setParsedData({});
        setExcelColumns({});
        setUploadedFiles([]);
        resolve({
          success: false,
          message:
            messageManager(nameConventions).BUDGET_UPLOAD.PARSING.NO_SHEET_DATA,
        });
        return;
      }

      const dataTypes = [];

      if (hasPersonnel) {
        dataTypes.push(PERSONNEL);
        excelCols[PERSONNEL] = getMappingRow(workbook.Sheets[PERSONNEL]);
        csvArrs[PERSONNEL] = utils.sheet_to_json(workbook.Sheets[PERSONNEL], {
          defval: '',
        });
        csvArrs[PERSONNEL].forEach((row) => {
          correctEligibleCells(row);
        });
      }

      if (hasNonPersonnel) {
        dataTypes.push(NONPERSONNEL);
        excelCols[NONPERSONNEL] = getMappingRow(workbook.Sheets[NONPERSONNEL]);
        csvArrs[NONPERSONNEL] = utils.sheet_to_json(
          workbook.Sheets[NONPERSONNEL],
          { defval: '' },
        );
        csvArrs[NONPERSONNEL].forEach((row) => {
          correctEligibleCells(row);
        });
      }

      csvArrs[PERSONNEL] &&
        csvArrs[PERSONNEL].forEach((x) => (x.rowId = crypto.randomUUID()));
      csvArrs[NONPERSONNEL] &&
        csvArrs[NONPERSONNEL].forEach((x) => (x.rowId = crypto.randomUUID()));

      setParsedData(csvArrs);
      setOriginalData(structuredClone(csvArrs));
      setExcelColumns(excelCols);
      setDataTypes(dataTypes);
      resolve({ success: true });
    };

    reader.readAsArrayBuffer(file);
  });
};

const correctEligibleCells = (row) => {
  if (row[EDIT]) {
    if (row[EDIT].trim().toLowerCase() === EDIT_TYPE_LABELS.EDIT.toLowerCase()) {
      row[EDIT_TYPE_LABELS.EDIT] = EDIT_TYPE_LABELS.EDIT;
    } else if (row[EDIT].trim().toLowerCase() === EDIT_TYPE_LABELS.ADD.toLowerCase()) {
      row[EDIT_TYPE_LABELS.EDIT] = EDIT_TYPE_LABELS.ADD;
    } else if (row[EDIT].trim().toLowerCase() === EDIT_TYPE_LABELS.DELETE.toLowerCase()) {
      row[EDIT_TYPE_LABELS.EDIT] = EDIT_TYPE_LABELS.DELETE;
    }
  }
  if (
    row[ONGOING_COST_COLUMN] === null ||
    row[ONGOING_COST_COLUMN] === ''
  ) {
    row[ONGOING_COST_COLUMN] = 0;
  }
  if (
    row[ONETIME_COST_COLUMN] === null ||
    row[ONETIME_COST_COLUMN] === ''
  ) {
    row[ONETIME_COST_COLUMN] = 0;
  }
  if (row[ONGOING_COST_COLUMN]) {
    row[ONGOING_COST_COLUMN] = roundToTwoDecimals(
      row[ONGOING_COST_COLUMN],
    );
  }
  if (row[ONETIME_COST_COLUMN]) {
    row[ONETIME_COST_COLUMN] = roundToTwoDecimals(
      row[ONETIME_COST_COLUMN],
    );
  }         
};

function rowsExist(workbook, sheet) {
  const worksheet = workbook.Sheets[sheet];

  if (!worksheet) {
    return false;
  }

  const rows = utils.sheet_to_row_object_array(worksheet);

  return (rows || []).length > 0;
}

function getMappingRow(sheet) {
  const re = /^[a-zA-Z]+1$/;
  const mappingRowFill = {};
  if (sheet) {
    for (const [key, values] of Object.entries(sheet)) {
      if (re.test(key)) {
        const value = values.v;
        const valueToLower = value.toLowerCase().replace(/[\t\n\r ]+$/g, '');
        if (!mappingRowFill[valueToLower]) {
          mappingRowFill[valueToLower] = value;
        }
      }
    }
  }
  return mappingRowFill;
}

export const getHeaderButtons = (handleNewUpload) => [
  {
    name: 'New upload',
    variant: 'text',
    onClickFunction: () => {
      handleNewUpload();
    },
    icon: 'upload',
    id: 1,
  },
];

export const generateBaseColumnDef = (colName, overrides = {}, mappedCols) => {
  const mappedField = mappedCols.get(colName);
  const baseColDef = {
    colId: colName,
    headerName: colName,
    field: mappedField,
    width: Math.max(colName.length * 15, 140),
    resizable: true,
    context: colName,
    tooltipField: colName,
    suppressMovable: true,
  };

  let extraConfig = {};

  if (colName === DURATION_TYPE_COLUMN) {
    extraConfig = {
      valueFormatter: (params) => getDurationTypeDisplayValue(params.value),
      valueParser: (params) => getDurationTypeDisplayValue(params.newValue),
      valueSetter: (params) => {
        params.data[mappedField] = getDurationTypeDisplayValue(params.newValue);
        return true;
      },
      cellEditorParams: {
        allowTyping: true,
        highlightMatch: true,
      },
    };
  }

  if (colName === EDIT) {
    extraConfig = {
      pinned: 'left',
      lockPosition: 'left',
      resizable: false,
      width: 100,
      cellEditor: 'agRichSelectCellEditor',
      
    };
  }

  return {
    ...baseColDef,
    ...extraConfig,
    ...overrides,
  };
};

export const generateValidationColumnDef = (
  colName,
  activeStep,
  mappedCols,
  dataTypes
) => {
  const baseColDef = generateBaseColumnDef(
    colName,
    { editable: true },
    mappedCols,
  );

  if (colName === EDIT) {
    return {
      ...baseColDef,
      cellEditor: 'agRichSelectCellEditor',
      cellEditorParams: {
        values: Object.values(EDIT_TYPE_LABELS),
        allowTyping: true,
        searchType: 'matchAny',
        highlightMatch: true,
        filterList: true
      },
    };
  }

  if (colName === VALIDATION) {
    return {
      ...baseColDef,
      pinned: 'right',
      editable: false,
      width: 150,
      lockPosition: 'right',
      resizable: false,
      field: VALIDATION,
    };
  }

  if (colName === DURATION_TYPE_COLUMN) {
    return {
      ...baseColDef,
      cellEditor: 'agRichSelectCellEditor',
      cellEditorParams: {
        values: Object.values(DURATION_TYPE_LABELS),
      },
    };
  }

  if (colName === ACCOUNT_TYPE_COLUMN) {
    return {
      ...baseColDef,
      cellEditor: 'agRichSelectCellEditor',
      cellEditorParams: {
        values:
          determineObjectTypeId(dataTypes, activeStep) === PERSONNEL ?
            [ACCOUNT_TYPE_LABELS.EXPENSE]
            : Object.values(ACCOUNT_TYPE_LABELS),
      },
    };
  }

  if (colName === ONGOING_COST_COLUMN || colName === ONETIME_COST_COLUMN) {
    return {
      ...baseColDef,
      cellEditorParams: {
        precision: 2,
      },
      valueSetter: (params) => {
        params.data[mappedCols.get(colName) || colName] = roundToTwoDecimals(params.newValue);
        return true;
      },
      valueParser: (params) => roundToTwoDecimals(params.newValue),
    };
  }

  return baseColDef;
};

const getDurationTypeDisplayValue = (value) => {
  const normalizedValue = normalizeDurationTypeValue(value);

  if (normalizedValue === DURATION_TYPES.ONGOING) {
    return DURATION_TYPE_LABELS.ONGOING;
  }

  if (normalizedValue === DURATION_TYPES.ONE_TIME) {
    return DURATION_TYPE_LABELS.ONE_TIME;
  }

  return value || value === 0 ? value.toString().trim() : '';
};

export const availableSelectOptions = (excelColumns, activeStep, dataTypes) =>
  Object.values(
    excelColumns[determineObjectTypeId(dataTypes, activeStep)] || {},
  );

export const determineObjectTypeId = (dataTypes, activeStep) => {
  if (activeStep === 0 || activeStep === 1) {
    if (dataTypes.includes(PERSONNEL)) {
      return PERSONNEL;
    }
    return NONPERSONNEL;
  }
  return NONPERSONNEL;
};

export const determineSteps = (dataTypes) => {
  if (dataTypes.includes(PERSONNEL) && dataTypes.includes(NONPERSONNEL)) {
    return uploadSteps.map((step) => ({ label: step.label }));
  } else if (
    dataTypes.includes(PERSONNEL) &&
    !dataTypes.includes(NONPERSONNEL)
  ) {
    return uploadSteps
      .filter((step) => step.step === PERSONNEL)
      .map((step) => ({ label: step.label }));
  } else if (
    !dataTypes.includes(PERSONNEL) &&
    dataTypes.includes(NONPERSONNEL)
  ) {
    return uploadSteps
      .filter((step) => step.step === NONPERSONNEL)
      .map((step) => ({ label: step.label }));
  }
};

export const getColumns = (
  templates,
  dataTypes,
  activeStep,
  personnelMappedCols,
  nonPersonnelMappedCols,
  setPersonnelMappedCols,
  setNonPersonnelMappedCols,
  excelColumns,
  dispatch,
) => {
  if (!templates.personnel && !templates.nonPersonnel) return [];
  const mappedCols =
    determineObjectTypeId(dataTypes, activeStep) === PERSONNEL ?
      personnelMappedCols
      : nonPersonnelMappedCols;

  const mappedColsSetter =
    determineObjectTypeId(dataTypes, activeStep) === PERSONNEL ?
      setPersonnelMappedCols
      : setNonPersonnelMappedCols;

  return Object.keys(
    determineObjectTypeId(dataTypes, activeStep) === PERSONNEL ?
      templates.personnel
      : templates.nonPersonnel,
  ).map((col) =>{
    return generateBaseColumnDef(
      col,
      {
        headerClass: !mappedCols.has(col) ? 'invalid' : '',
        floatingFilter: true,
        floatingFilterComponent: CustomFloatingFilter,
        floatingFilterComponentParams: {
          value: mappedCols.get(col) || '',
          options: availableSelectOptions(excelColumns, activeStep, dataTypes),
          onValueChange: (col, selectedColKey) => {
            handleSelectChange(col, selectedColKey, mappedColsSetter, dispatch, determineObjectTypeId(dataTypes, activeStep) === PERSONNEL ?
              templates.personnel
              : templates.nonPersonnel);
          },
        },
      },
      mappedCols,
    );}
  );
};

const handleSelectChange = (
  mappedCol,
  selectedColKey,
  setMappedCols,
  dispatch,
  template
) => {
  dispatch({
    type: REDUCER_ACTION_TYPES.FIRE_UNSAVED_CHANGES_DIALOG,
    payload: true,
  });

  setMappedCols((prevMapping) => {
    const newMapping = new Map();
    const templateKeys = Object.keys(template);

    // Iterate over the keys of the template object in exact order
    for (const key of templateKeys) {
      if (key === mappedCol) {
        newMapping.set(key, selectedColKey);
      } else if (prevMapping.has(key)) {
        newMapping.set(key, prevMapping.get(key));
      }
    }

    return newMapping;
  });
};

export const determineObjectType = (dataTypes, activeStep) => {
  if (activeStep === 0) {
    if (dataTypes.includes(PERSONNEL)) {
      return PERSONNEL_OBJECT_TYPE_ID;
    }
    return NONPERSONNEL_OBJECT_TYPE_ID;
  }
  return NONPERSONNEL_OBJECT_TYPE_ID;
};

export const isMappingNameDuplicated = (mappingName, existingMappingNames) => {
  return existingMappingNames.find((name) => name === mappingName.trim());
};

export const isNameInvalid = (mappingName, existingMappingNames) => {
  return (
    !mappingName.trim().length ||
    isMappingNameDuplicated(mappingName, existingMappingNames)
  );
};

export const decideActionOnCancelOrGoBack = (
  state,
  dispatch,
  determineLastStepAndValidations,
  resetContext,
  cancelChanges,
  handleSubmit,
  gridRef,
  newUpload,
  setNewUpload,
  setOpenUploadDialog
) => {
  if (state.saveUnsavedChanges) {
    // save fired
    if (determineLastStepAndValidations(gridRef)) {
      handleSubmit();
      resetContext();
      dispatch({
        type: REDUCER_ACTION_TYPES.SAVE_UNSAVED_CHANGES,
        payload: false,
      });
    } else {
      ToastComponent.present({
        message: messageManager().BUDGET_UPLOAD.GENERAL.INCOMPLETE_PROCESS,
      });
      if (state.backButtonFired) {
        resetContext();
        dispatch({
          type: REDUCER_ACTION_TYPES.BACK_BUTTON_FIRED,
          payload: false,
        });
        dispatch({
          type: REDUCER_ACTION_TYPES.SAVE_UNSAVED_CHANGES,
          payload: false,
        });
        if (newUpload) {
          setOpenUploadDialog(true);
          setNewUpload(false);
        }
      } else {
        dispatch({
          type: REDUCER_ACTION_TYPES.FIRE_UNSAVED_CHANGES_DIALOG,
          payload: true,
        });
        dispatch({
          type: REDUCER_ACTION_TYPES.SAVE_UNSAVED_CHANGES,
          payload: false,
        });
      }
    }
  } else if (state.discardUnsavedChanges) {
    // discard fired
    if (state.backButtonFired) {
      resetContext();
      dispatch({
        type: REDUCER_ACTION_TYPES.BACK_BUTTON_FIRED,
        payload: false,
      });
      if (newUpload) {
        setOpenUploadDialog(true);
        setNewUpload(false);
      }
    } else {
      cancelChanges();
    }

    dispatch({ type: REDUCER_ACTION_TYPES.SET_USER_NAVIGATED, payload: false });
    dispatch({
      type: REDUCER_ACTION_TYPES.SAVE_UNSAVED_CHANGES,
      payload: false,
    });
    dispatch({
      type: REDUCER_ACTION_TYPES.DISCARD_UNSAVED_CHANGES,
      payload: false,
    });
  }
};

export const pickMappedCols = (
  personnelMappedCols,
  nonPersonnelMappedCols,
  dataTypes,
  activeStep,
) => {
  return determineObjectTypeId(dataTypes, activeStep) === PERSONNEL ?
    personnelMappedCols
    : nonPersonnelMappedCols;
};

export const BUDGET_NAME_AND_YEAR_DELIMITER = '--\\o.0/--';

export const determineBudgetNameAndYear = (gridRef, parsedData, mappedCols) => {
  const firstExcelRowId = parsedData[0].rowId;  // get the rowId of the excel's first row from first sheet
  const firstRowData = gridRef.current.api.getRowNode(firstExcelRowId); // get the row from grid using that rowId we gathered above. 
  const budgetName = getRowValue(firstRowData.data, ROW_BUDGET_NAME_DEFAULT_NAME, mappedCols); // (why this, because in grid the first row is not guarantied to be the first one from the excel - "post sort" remember)
  const year = getRowValue(firstRowData.data, ROW_YEAR_DEFAULT_NAME, mappedCols);
  return `${budgetName}${BUDGET_NAME_AND_YEAR_DELIMITER}${year}`; // this  :)
};

export const getCurrentRowsForValidation = (gridRef, mappedCols) => {
  const validationEligibleRows = [];
  gridRef.current.api.forEachNode((node) => {
    node.data.Error = '';
    node.data.Validation = '';

    if (node.data[mappedCols.get(EDIT)]) {
      validationEligibleRows.push(node.data);
    }
  });
  return validationEligibleRows;
};

export const getCurrentRowsForErrorReport = (gridRef) => {
  const rows = [];
  gridRef.current.api.forEachNode((node) => {
    rows.push(node.data);
  });
  return rows;
};

export const validateRows = async (
  rows,
  gridRef,
  dataTypes,
  activeStep,
  userGroupConvention,
  costModel,
  budgetNameAndYear,
  setValidRows,
  setInvalidRows,
  setDisablePrimaryButton,
  mappedCols,
) => {
  const validatedRows = rows.map((r) =>
    validateSingleRow(
      r,
      dataTypes,
      activeStep,
      userGroupConvention,
      budgetNameAndYear,
      mappedCols,
    ),
  );
  
  const sanitizedRows = findDuplicatesAgainstEachOther(
    validatedRows,
    mapPropsToFileProps(getUniqueProps(costModel), mappedCols),
  );

  const rowsForBackEndValidation = sanitizedRows.filter((r) => !r.Error);

  const payload = buildRowsForBackEndValidation(
    rowsForBackEndValidation,
    dataTypes,
    activeStep,
    costModel,
    mappedCols
  );

  const response = await validateAccounts(payload);
  const validationResults = response.data.validations;

  const errorMap = new Map(
    validationResults
      .filter((item) => !item.isValid)
      .map((item) => [item.rowId, item.errorMessage]),
  );

  if (errorMap.size > 0) {
    sanitizedRows.forEach((row) => {
      if (row.rowId && errorMap.has(row.rowId)) {
        row.Error = errorMap.get(row.rowId);
        row.Validation =
          messageManager().BUDGET_UPLOAD.VALIDATION.FAIL_VALIDATION;
      }
    });
  }
  
  if (gridRef.current?.api) {
    gridRef.current.api.applyTransaction({ update: sanitizedRows });
  }

  const {validRowsCount, errorRowsCount} = determineValidAndInvalidRows(gridRef);
  setValidRows(validRowsCount);
  setInvalidRows(errorRowsCount);
  (activeStep===1 || activeStep === 3 ? setDisablePrimaryButton(errorRowsCount>0) : setDisablePrimaryButton(false));
};

const mapPropsToFileProps = (uniquePropsArray, mappedCols) => {
  const propsArray = [];

  for (const defaultName of uniquePropsArray) {
    const mappedName = mappedCols.get(defaultName) || defaultName;
    propsArray.push(mappedName);
  }

  return propsArray;
};

const findDuplicatesAgainstEachOther = (rows, uniquePropsArray) => {
  const map = new Map();

  for (const row of rows) {
    const key = uniquePropsArray
      .map((prop) =>
        String(row[prop] ?? '')
          .trim()
          .toLowerCase(),
      )
      .join('|');

    if (!map.has(key)) {
      map.set(key, []);
    }
    map.get(key).push(row);
  }

  for (const matchedRows of map.values()) {
    const isDuplicate = matchedRows.length > 1;
    for (const row of matchedRows) {
      if (isDuplicate) {
        row.Error =
          messageManager().BUDGET_UPLOAD.VALIDATION.FRONT_END_DUPLICATE;
        row.Validation = messageManager().BUDGET_UPLOAD.VALIDATION.FAIL_VALIDATION;
      }
    }
  }

  return rows;
};

const validateSingleRow = (
  row,
  dataTypes,
  activeStep,
  userGroupConvention,
  budgetNameAndYear,
  mappedCols
) => {
  //Edit Column
  const editColumnName = getEditColumnName(mappedCols);
  const rowEdit = row[editColumnName];
  const editValidation = validateEditColumn(rowEdit);

  if (!editValidation.isValid) {
    return {
      ...row,
      Error: editValidation.errorMessage,
      Validation: editValidation.outcome,
    };
  }

  //Budget name
  const budgetNameColumnName = getBudgetNameColumnName(mappedCols);
  const rowBudgetName = row[budgetNameColumnName];
  const budgetNameValidation = validateBudgetNameColumn(rowBudgetName);

  if (!budgetNameValidation.isValid) {
    return {
      ...row,
      Error: budgetNameValidation.errorMessage,
      Validation: budgetNameValidation.outcome,
    };
  }

  // Year column
  const yearColumnName = getYearColumnName(mappedCols);
  const rowYear = row[yearColumnName];
  const yearValidation = validateYearColumn(rowYear);

  if (!yearValidation.isValid) {
    return {
      ...row,
      Error: yearValidation.errorMessage,
      Validation: yearValidation.outcome,
    };
  }

  //ItemId column
  const itemIDColumnName = getItemIDColumnName(mappedCols);
  const rowItemID = row[itemIDColumnName];
  const itemIDValidation = validateItemIdColumn(
    rowItemID,
    rowEdit,
  );

  if (!itemIDValidation.isValid) {
    return {
      ...row,
      Error: itemIDValidation.errorMessage,
      Validation: itemIDValidation.outcome,
    };
  }

  //AccountID column
  const accountIDColumnName = getAccountIDColumnName(mappedCols);
  const rowAccountID = row[accountIDColumnName];
  const accountIDValidation = validateAccountIdColumn(
    rowAccountID,
    rowEdit,
  );

  if (!accountIDValidation.isValid) {
    return {
      ...row,
      Error: accountIDValidation.errorMessage,
      Validation: accountIDValidation.outcome,
    };
  }

  //AccountType column
  const accountTypeColumnName = getAccountTypeColumnName(mappedCols);
  const rowAccountType = row[accountTypeColumnName];
  const accountTypeValidation = validateAccountTypeColumn(
    rowAccountType,
    rowEdit,
    determineObjectTypeId(dataTypes, activeStep),
  );

  if (!accountTypeValidation.isValid) {
    return {
      ...row,
      Error: accountTypeValidation.errorMessage,
      Validation: accountTypeValidation.outcome,
    };
  }

  //Duration Type column
  const durationTypeColumnName = getDurationTypeColumnName(mappedCols);
  const rowDurationType = row[durationTypeColumnName];
  const durationTypeValidation = validateDurationTypeColumn(
    rowDurationType,
    rowEdit,
  );

  if (!durationTypeValidation.isValid) {
    return {
      ...row,
      Error: durationTypeValidation.errorMessage,
      Validation: durationTypeValidation.outcome,
    };
  }

  //Ongoing cost column
  const ongoingCostColumnName = getOngoingCostColumnName(mappedCols);
  const rowOngoingCost = row[ongoingCostColumnName];
  const ongoingCostValidation = validateOngoingCostColumn(
    rowOngoingCost,
    rowEdit,
  );

  if (!ongoingCostValidation.isValid) {
    return {
      ...row,
      Error: ongoingCostValidation.errorMessage,
      Validation: ongoingCostValidation.outcome,
    };
  }

  //One cost column
  const onetimeCostColumnName = getOnetimeCostColumnName(mappedCols);
  const rowOnetimeCost = row[onetimeCostColumnName];
  const oneCostValidation = validateOneTimeCostColumn(
    rowOnetimeCost,
    rowEdit,
  );

  if (!oneCostValidation.isValid) {
    return {
      ...row,
      Error: oneCostValidation.errorMessage,
      Validation: oneCostValidation.outcome,
    };
  }

  //Number of items column
  const numberOfItemsColumnName = getNumberOfTimesColumnName(mappedCols);
  const rowNumberOfItems = row[numberOfItemsColumnName];
  const numberOfItemsValidation = validateNumberOfItemsColumn(
    rowNumberOfItems,
    rowEdit,
  );

  if (!numberOfItemsValidation.isValid) {
    return {
      ...row,
      Error: numberOfItemsValidation.errorMessage,
      Validation: numberOfItemsValidation.outcome,
    };
  }

  //cost center columns
  const costCenterDefaultName = getCostCenterColumnName(mappedCols, userGroupConvention);
  const rowCostCenter = row[costCenterDefaultName];
  const costCenterValidation = validateCostCenterColumn(
    rowCostCenter,
    rowEdit,
    userGroupConvention,
  );

  if (!costCenterValidation.isValid) {
    return {
      ...row,
      Error: costCenterValidation.errorMessage,
      Validation: costCenterValidation.outcome,
    };
  }

  //Fund column
  const fundColumnName = getFundColumnName(mappedCols);
  const fundRow = row[fundColumnName];
  const fundValidation = validateFundTypeColumn(
    fundRow,
    rowEdit,
  );

  if (!fundValidation.isValid) {
    return {
      ...row,
      Error: fundValidation.errorMessage,
      Validation: fundValidation.outcome,
    };
  }

  //Budget name and year check
  const name = rowBudgetName.trim().toLowerCase();
  const year = rowYear;
  const rowsBudgetNameAndYear = `${name}${BUDGET_NAME_AND_YEAR_DELIMITER}${year}`;

  if (rowsBudgetNameAndYear !== budgetNameAndYear.toLowerCase()) {
    return {
      ...row,
      Error: messageManager().BUDGET_UPLOAD.VALIDATION.NOT_SAME_BUDGET(budgetNameAndYear.split(BUDGET_NAME_AND_YEAR_DELIMITER).join(' ')),
      Validation: messageManager().BUDGET_UPLOAD.VALIDATION.FAIL_VALIDATION,
    };
  }

  return { ...row, Error: '', Validation: messageManager().BUDGET_UPLOAD.VALIDATION.PASS_VALIDATION };
};

const buildRowsForBackEndValidation = (
  rows,
  dataTypes,
  activeStep,
  costModel,
  mappedCols
) => {
  return rows.map((r) => {
    const orgLevelsString = buildOrgLevelsString(
      r,
      costModel.organizationLevelTypes,
      mappedCols
    );
    const objCategoriesString = buildObjCategoriesString(
      r,
      costModel.objectCategoryTypes,
      dataTypes,
      activeStep,
      mappedCols
    );
    const { addDuplicateOfAnItem, editDuplicateOfAnItem } = buildUniqueStrings(
      r,
      orgLevelsString,
      objCategoriesString,
      dataTypes,
      activeStep,
      mappedCols
    );

    return {
      accountId: getRowValue(r, ROW_ACCOUNT_ID_DEFAULT_NAME, mappedCols).toString(),
      edit: getRowValue(r, ROW_EDIT_DEFAULT_NAME, mappedCols).toLowerCase(),
      itemId: getRowValue(r, ROW_ITEM_ID_DEFAULT_NAME, mappedCols).toString(),
      rowId: r.rowId,
      addDuplicateOfAnItem,
      editDuplicateOfAnItem,
    };
  });
};

const buildUniqueStrings = (
  row,
  orgLevelsString,
  objCategoriesString,
  dataTypes,
  activeStep,
  mappedCols
) => {
  const durationType =
    getRowValue(row, ROW_DURATION_TYPE_DEFAULT_NAME, mappedCols).toLowerCase() === 'ongoing' ?
      DURATION_TYPE_ID.ONGOING
      : DURATION_TYPE_ID.ONETIME;

  const objectTypeId =
    determineObjectTypeId(dataTypes, activeStep)?.toLowerCase() ?? '';

  const addParts = [
    getRowValue(row, ROW_BUDGET_NAME_DEFAULT_NAME, mappedCols) ?? '',
    getRowValue(row, ROW_YEAR_DEFAULT_NAME, mappedCols) ?? '',
    getRowValue(row, ROW_ACCOUNT_CODE_DEFAULT_NAME, mappedCols) ?? '',
    getRowValue(row, ROW_ACCOUNT_TYPE_DEFAULT_NAME, mappedCols) ?? '',
    durationType,
    getRowValue(row, ROW_FUND_DEFAULT_NAME, mappedCols) ?? '',
    orgLevelsString,
    objCategoriesString,
    objectTypeId,
  ];

  const acctID = getRowValue(row, ROW_ACCOUNT_ID_DEFAULT_NAME, mappedCols);

  const editParts = [
    ...addParts.slice(0, 2),
    acctID ?? '',
    ...addParts.slice(2),
  ];

  return {
    addDuplicateOfAnItem: addParts.join('|').toLowerCase(),
    editDuplicateOfAnItem: editParts.join('|').toLowerCase(),
  };
};

const buildOrgLevelsString = (row, levels, mappedCols) => {
  return levels.map((level) => row[mappedCols.get(level.name)] ?? '').join('|');
};

const buildObjCategoriesString = (row, categories, dataTypes, activeStep, mappedCols) => {
  const targetTypeId = determineObjectTypeId(dataTypes, activeStep);

  return categories
    .filter((cat) => cat.objectType?.name === targetTypeId)
    .map((c) => row[mappedCols.get(c.name)] ?? '')
    .join('|');
};

const determineValidAndInvalidRows = (gridRef) => {
  let validRowsCount = 0;
  let errorRowsCount = 0;
  gridRef.current.api.forEachNode(node=> {
    if (node.data.Validation === messageManager().BUDGET_UPLOAD.VALIDATION.PASS_VALIDATION) {
      validRowsCount++;
    } else if (node.data.Error) {
      errorRowsCount++;
    }
  });
  return {validRowsCount, errorRowsCount};
};

export const isLastStep = (dataTypes, activeStep) => {
  return dataTypes.length===1 && activeStep === 1 || dataTypes.length === 2 && activeStep === 3;
};

export const isMappingStep = (dataTypes, activeStep) => {
  return dataTypes.length===1 && activeStep === 0 || dataTypes.length === 2 && (activeStep === 2 || activeStep === 0);
};

export const isPersonnelValidationStep = (dataTypes, activeStep) => {
  return dataTypes.length===2 && activeStep === 1;
};

export const gatherRowsToUpload = (dataTypes, parsedData, personnelMappedCols, nonPersonnelMappedCols, gridRef, activeStep) => {
  let personnelData = [];
  let nonPersonnelData = [];
  let personnelToSend = [];
  let nonPersonnelToSend = [];

  if (dataTypes.length === 2) {
    // 1. Process PERSONNEL from parsedData
    const personnelEditRowName = getEditColumnName(personnelMappedCols);
    const editedPersonnelRows = (parsedData[PERSONNEL] || []).filter((row) => row[personnelEditRowName]);
    personnelToSend = mapDataRows(editedPersonnelRows, personnelMappedCols);

    // 2. Process NONPERSONNEL from AG Grid
    const nonPersonnelEditRowName = getEditColumnName(nonPersonnelMappedCols);
    gridRef.current?.api?.forEachNode((node) => {
      if ((node.data || {})[nonPersonnelEditRowName]) {
        nonPersonnelData.push(node.data);
      }
    });
    nonPersonnelToSend = mapDataRows(nonPersonnelData, nonPersonnelMappedCols);
  } else {
    // Single type mode
    const targetObjectType = determineObjectTypeId(dataTypes, activeStep);
    const editRowName = getEditColumnName(targetObjectType === PERSONNEL ? personnelMappedCols : nonPersonnelMappedCols);
    gridRef.current?.api?.forEachNode((node) => {
      if ((node.data || {})[editRowName]) {
        if (targetObjectType === PERSONNEL) {
          personnelData.push(node.data);
        } else {
          nonPersonnelData.push(node.data);
        }
      }
    });

    personnelToSend = mapDataRows(personnelData, personnelMappedCols);
    nonPersonnelToSend = mapDataRows(nonPersonnelData, nonPersonnelMappedCols);
  }
  return { personnelToSend, nonPersonnelToSend };
};

const mapDataRows = (rows, mappingMap) => {
  if (!rows || !rows.length || !mappingMap || !mappingMap.size) return [];
  // TODO 2026-09-11: check if data is being loaded from columns with the mapped names, and not the other way around!
  return rows.map((row) => {
    const mappedRow = {};
    // JS Map iteration: value comes first, then key in .forEach((value, key) => {})
    mappingMap.forEach((sourceProp, targetKey) => {
      mappedRow[targetKey] = row[sourceProp];
    });
    return mappedRow;
  });
};

export const checkIfUnmappedCols = (templates, dataTypes, activeStep, personnelMappedCols, nonPersonnelMappedCols) => {
  const templateObject =
      templates[
        determineObjectTypeId(dataTypes, activeStep) === PERSONNEL ?
          'personnel'
          : 'nonPersonnel'
      ];
  const mappedCols =
    determineObjectTypeId(dataTypes, activeStep) === PERSONNEL ?
      personnelMappedCols
      : nonPersonnelMappedCols;

  const currentTemplate = Object.keys(templateObject || {});
  const unmappedCols = currentTemplate.filter(
    (col) => !mappedCols.get(col),
  );
  return unmappedCols;
};

export const handleDownloadBudgetUploadStatusReport = (
  gridRows,
  gridColumns,
  fileFormat,
  fileName,
  sheetName,
  user,
) => {
  const cleanedColumns = gridColumns
    ? gridColumns.filter((col) => col && (col.field || col.headerName))
    : [];
  cleanedColumns.push({
    headerName: 'Error',
    field: 'Error',
  });

  const rowCopy = sortForReportExport(gridRows);
  const totalRows = rowCopy.length;

  for (let i = 0; i < totalRows; i += CHUNK_SIZE) {
    const isLast = i + CHUNK_SIZE >= totalRows;
    const chunk = rowCopy.slice(i, i + CHUNK_SIZE);

    if (isLast) {
      emitBudgetUploadStatusReportChunk({
        user,
        environment: process.env.ENVIRONMENT,
        gridColumns: cleanedColumns,
        fileFormat,
        fileName,
        sheetName,
        chunk,
        isLast: true,
      });
    } else {
      emitBudgetUploadStatusReportChunk({
        chunk,
        isLast: false,
      });
    }
  }

  emitDocumentLoading('Budget upload status report', `.${fileFormat}`);
};

const sortForReportExport = (rows) => {
  const errors = [];
  const nonErrors = [];

  for (let i = 0; i < rows.length; i++) {
    const rowCopy = structuredClone(rows[i]);
    if (rowCopy && rowCopy.Error) {
      errors.push(rowCopy);
    } else {
      nonErrors.push(rowCopy);
    }
  }

  return errors.concat(nonErrors);
};

export const setUserAndPath = async (
  user,
  res,
  navigate,
  setUser,
  setOrganizationBudgets,
) => {
  const _user = structuredClone(user);
  const isActiveBudgetExist = res.data.updatedBudgets.some(budget => budget.id === _user.activeBudget?.id);
  
  if (!isActiveBudgetExist) {
    _user.activeBudget = res.data.updatedBudgets[0] || null;
    navigate(`/${_user.activeOrganization}/${res.data.updatedBudgets[0]?.id || 'no-budget'}/budgetSummary`, {
      replace: true,
    });
  }
  _user.organizationBudgets = res.data.updatedBudgets;
  setOrganizationBudgets(res.data.updatedBudgets);
  _user.rawCostCenterArray = res.data.costCenters;
  _user.costCenters = res.data.costCenters.map(cc=>cc.name);

  if ((_user.role == USER_ROLE.ADMIN || _user.role == USER_ROLE.SUPER_USER) && res.data.newCostCenters.length) {
    const activeCostCenters = (_user.activeCostCenters || []).concat(res.data.newCostCenters.map(cc => cc.name));

    _user.activeCostCenters = activeCostCenters;
    _user.activeCostCenterIds = (_user.activeCostCenterIds || []).concat(res.data.newCostCenters.map(cc => cc.id));
    
    sessionStorage.setItem(SESSION_STORAGE_KEYS.ACTIVE_COST_CENTERS, JSON.stringify(activeCostCenters));
    await setActiveCostCenters(activeCostCenters);
  }
  setUser(_user);
};

export const normalizeSavedMapping = (entries, excelColumns, mappingObj) => {
  for (const [key] of entries) {
    if (excelColumns[key.toLowerCase()]) {
      mappingObj.mappedValues.set(key, excelColumns[key.toLowerCase()]);
    } else {
      mappingObj.mappedValues.delete(key);
    }
  }
};

const ROW_EDIT_DEFAULT_NAME = 'Edit';
const ROW_BUDGET_NAME_DEFAULT_NAME = 'BudgetName';
const ROW_YEAR_DEFAULT_NAME = 'Year';
const ROW_ITEM_ID_DEFAULT_NAME = 'ItemID';
const ROW_ACCOUNT_ID_DEFAULT_NAME = 'AcctID';
const ROW_ACCOUNT_TYPE_DEFAULT_NAME = 'Account type';
const ROW_DURATION_TYPE_DEFAULT_NAME = 'Duration type';
const ROW_ONGOING_COST_DEFAULT_NAME = 'Ongoing_cost';
const ROW_ONETIME_COST_DEFAULT_NAME = 'Onetime_cost';
const ROW_NUMBER_OF_ITEMS_DEFAULT_NAME = 'Number of items';
const ROW_COST_CENTER_DEFAULT_NAME = 'User group';
const ROW_FUND_DEFAULT_NAME = 'Fund';
const ROW_ACCOUNT_CODE_DEFAULT_NAME = 'AcctCode';

export const getEditColumnName = (mappedCols) => {
  return mappedCols.get(ROW_EDIT_DEFAULT_NAME) || ROW_EDIT_DEFAULT_NAME;
};

export const getBudgetNameColumnName = (mappedCols) => {
  return mappedCols.get(ROW_BUDGET_NAME_DEFAULT_NAME) || ROW_BUDGET_NAME_DEFAULT_NAME;
};

export const getYearColumnName = (mappedCols) => {
  return mappedCols.get(ROW_YEAR_DEFAULT_NAME) || ROW_YEAR_DEFAULT_NAME;
};

export const getItemIDColumnName = (mappedCols) => {
  return mappedCols.get(ROW_ITEM_ID_DEFAULT_NAME) || ROW_ITEM_ID_DEFAULT_NAME;
};

export const getAccountIDColumnName = (mappedCols) => {
  return mappedCols.get(ROW_ACCOUNT_ID_DEFAULT_NAME) || ROW_ACCOUNT_ID_DEFAULT_NAME;
};

export const getAccountTypeColumnName = (mappedCols) => {
  return mappedCols.get(ROW_ACCOUNT_TYPE_DEFAULT_NAME) || ROW_ACCOUNT_TYPE_DEFAULT_NAME;
};

export const getDurationTypeColumnName = (mappedCols) => {
  return mappedCols.get(ROW_DURATION_TYPE_DEFAULT_NAME) || ROW_DURATION_TYPE_DEFAULT_NAME;
};

export const getOngoingCostColumnName = (mappedCols) => {
  return mappedCols.get(ROW_ONGOING_COST_DEFAULT_NAME) || ROW_ONGOING_COST_DEFAULT_NAME;
};

export const getOnetimeCostColumnName = (mappedCols) => {
  return mappedCols.get(ROW_ONETIME_COST_DEFAULT_NAME) || ROW_ONETIME_COST_DEFAULT_NAME;
};

export const getNumberOfTimesColumnName = (mappedCols) => {
  return mappedCols.get(ROW_NUMBER_OF_ITEMS_DEFAULT_NAME) || ROW_NUMBER_OF_ITEMS_DEFAULT_NAME;
};

export const getCostCenterColumnName = (mappedCols, userGroupConvention) => {
  return mappedCols.get(userGroupConvention) || mappedCols.get(ROW_COST_CENTER_DEFAULT_NAME) || ROW_COST_CENTER_DEFAULT_NAME;
};

export const getFundColumnName = (mappedCols) => {
  return mappedCols.get(ROW_FUND_DEFAULT_NAME) || ROW_FUND_DEFAULT_NAME;
};

export const getAcctCodeColumnName = (mappedCols) => {
  return mappedCols.get(ROW_ACCOUNT_CODE_DEFAULT_NAME) || ROW_ACCOUNT_CODE_DEFAULT_NAME;
};

export const getRowValue = (row, identifier, mappedCols, userGroupConvention) => {
  let rowValue = '';
  let columnName = '';

  switch (identifier) {
    case ROW_EDIT_DEFAULT_NAME: columnName = getEditColumnName(mappedCols); break;
    case ROW_BUDGET_NAME_DEFAULT_NAME: columnName = getBudgetNameColumnName(mappedCols); break;
    case ROW_YEAR_DEFAULT_NAME: columnName = getYearColumnName(mappedCols); break;
    case ROW_ITEM_ID_DEFAULT_NAME: columnName = getItemIDColumnName(mappedCols); break;
    case ROW_ACCOUNT_ID_DEFAULT_NAME: columnName = getAccountIDColumnName(mappedCols); break;
    case ROW_ACCOUNT_TYPE_DEFAULT_NAME: columnName = getAccountTypeColumnName(mappedCols); break;
    case ROW_DURATION_TYPE_DEFAULT_NAME: columnName = getDurationTypeColumnName(mappedCols); break;
    case ROW_ONGOING_COST_DEFAULT_NAME: columnName = getOngoingCostColumnName(mappedCols); break;
    case ROW_ONETIME_COST_DEFAULT_NAME: columnName = getOnetimeCostColumnName(mappedCols); break;
    case ROW_NUMBER_OF_ITEMS_DEFAULT_NAME: columnName = getNumberOfTimesColumnName(mappedCols); break;
    case ROW_COST_CENTER_DEFAULT_NAME: columnName = getCostCenterColumnName(mappedCols, userGroupConvention); break;
    case ROW_FUND_DEFAULT_NAME: columnName = getFundColumnName(mappedCols); break;
    case ROW_ACCOUNT_CODE_DEFAULT_NAME: columnName = getAcctCodeColumnName(mappedCols); break;
    default: columnName = '';
  }

  if (columnName) rowValue = row[columnName];
  return rowValue;
};
